import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';

export interface WorkoutCaloriesBurn {
  perUnit: number | null;
  metValue: number | null;
}

export interface RecognizeWorkoutResponse {
  type: 'strength' | 'cardio' | 'stretching' | 'unrecognized';
  name: string;
  totalAmount?: number;
  unit?: string;
  intensity?: 'low' | 'medium' | 'high';
  scheduledLdt: string; // local datetime string (no Z suffix)
  originalText?: string;
  caloriesBurn?: WorkoutCaloriesBurn;
}

export interface RecognizeWorkoutDto {
  text: string;
  contextDate: string; // T2 — local datetime, no Z (YYYY-MM-DDTHH:mm:ss)
  force?: boolean;
}

export interface RecognizeWorkoutResult {
  suggestedTitle: string;
  items: RecognizeWorkoutResponse[];
}

export interface EstimateCaloriesDto {
  items: Array<{
    name: string;
    type?: string;
    totalAmount?: number;
    unit?: string;
    intensity?: string;
  }>;
  force?: boolean;
}

export interface CreateWorkoutEventsBulkDto {
  startFromStep?: 1 | 2 | 3;
  items?: RecognizeWorkoutResponse[];
  text?: string;
  force?: boolean;
  calendarId?: number;
  rawInput?: string;
  scheduledLdt: string; // T2 — local datetime, no Z
  title?: string;
  isCompleted?: boolean;
}

class WorkoutAnalyzerService {
  private readonly BASE_URL = '/ai/workout-analyzer';

  async recognize(payload: RecognizeWorkoutDto, clientId?: string | number): Promise<RecognizeWorkoutResult> {
    const url = clientId
      ? `/client/${clientId}${this.BASE_URL}/extract`
      : `${this.BASE_URL}/extract`;
    const response = await api.post<ApiResponse<RecognizeWorkoutResult>>(url, payload);
    return unwrapApiResponse<RecognizeWorkoutResult>(response);
  }

  async estimateCalories(payload: EstimateCaloriesDto, clientId?: string | number): Promise<RecognizeWorkoutResponse[]> {
    const url = clientId
      ? `/client/${clientId}${this.BASE_URL}/estimate-calories`
      : `${this.BASE_URL}/estimate-calories`;
    const response = await api.post<ApiResponse<RecognizeWorkoutResponse[]>>(url, payload);
    return unwrapApiResponse<RecognizeWorkoutResponse[]>(response);
  }

  async createEventsBulk(payload: CreateWorkoutEventsBulkDto): Promise<any[]> {
    const response = await api.post<ApiResponse<any[]>>(
      '/calendars/events/workout/bulk',
      payload
    );
    return unwrapApiResponse<any[]>(response);
  }

  async createEventsComplex(payload: CreateWorkoutEventsBulkDto, clientId?: string | number): Promise<any> {
    const url = clientId
      ? `/client/${clientId}/calendar/events/workout/complex`
      : '/calendars/events/workout/complex';
    const response = await api.post<ApiResponse<any>>(url, payload);
    return unwrapApiResponse<any>(response);
  }

  async updateEventsComplex(eventId: number, payload: CreateWorkoutEventsBulkDto): Promise<any> {
    const response = await api.patch<ApiResponse<any>>(`/calendars/events/${eventId}/workout/complex`, payload);
    return unwrapApiResponse<any>(response);
  }
}

export const workoutAnalyzerService = new WorkoutAnalyzerService();
